// ============================================================================
// Automatic field-placement suggestions.
//
// On upload, scan the document text for the labels that mark a signature
// block — "Authorized Signature:", "Print Name", "Date Signed" — and PROPOSE
// placements for the sender to confirm. Nothing is ever placed without
// review, per the Documenso/DocuSeal model.
//
// A NOTE ON WHAT THIS IS. This is a heuristic pattern matcher, not a language
// model. That is a deliberate choice, not a shortcut: signature-block labels
// are a small, stable, highly conventional vocabulary, and a deterministic
// matcher is faster, free, offline-capable, and — most importantly —
// reproducible, so the same contract always yields the same proposal. An LLM
// would add latency and non-determinism to a problem that does not need it.
// The report says the same thing; nobody should read "AI-assisted" here and
// imagine more than is present.
//
// The output is deliberately conservative. A missed field costs the sender
// one drag; a wrong field placed on a client contract costs more.
// ============================================================================

import type { FieldType } from './envelope-lib';
import {
  ANCHOR_DEFAULT_SIZE, AnchorPlacement, TextRun, isWholeStringMatch,
} from './anchor-placement';

export interface DetectionRule {
  fieldType: FieldType;
  /** Lower-cased label fragments that mark this kind of field. */
  labels: string[];
  /** Confidence when matched as a clear label (ends with ':' or a rule). */
  confidence: 'high' | 'medium';
}

/**
 * Ordered most-specific first: "date signed" must win over "date", and
 * "print name" over "name", or the more general rule swallows the specific
 * one and the sender gets the wrong field type.
 */
export const DETECTION_RULES: DetectionRule[] = [
  { fieldType: 'signature', labels: ['authorized signature', 'client signature', 'contractor signature', 'signature of', 'sign here', 'signature'], confidence: 'high' },
  { fieldType: 'initials', labels: ['initials', 'initial here', 'initial'], confidence: 'high' },
  { fieldType: 'date', labels: ['date signed', 'date of signature', 'dated', 'date'], confidence: 'high' },
  { fieldType: 'name', labels: ['printed name', 'print name', 'name of signer', 'full name'], confidence: 'high' },
  { fieldType: 'text', labels: ['title', 'position', 'company name', 'company'], confidence: 'medium' },
  { fieldType: 'email', labels: ['email address', 'e-mail', 'email'], confidence: 'medium' },
];

export interface Suggestion extends AnchorPlacement {
  fieldType: FieldType;
  /** The document text that triggered this suggestion. */
  matchedLabel: string;
  confidence: 'high' | 'medium';
}

/** Labels are short. Body prose that happens to contain "signature" is not. */
const MAX_LABEL_CHARS = 48;

/**
 * Does this run read like a form label rather than prose? A label either ends
 * with a colon, trails into an underscore rule, or is a short standalone
 * phrase. Requiring one of those is what keeps "...this signature is binding
 * upon both parties" out of the results.
 */
export function looksLikeLabel(text: string): boolean {
  const t = text.trim();
  if (!t || t.length > MAX_LABEL_CHARS) return false;
  if (/[:_]\s*$/.test(t)) return true;
  if (/_{2,}/.test(t)) return true;
  // A short phrase with no sentence punctuation reads as a label.
  return t.split(/\s+/).length <= 4 && !/[.!?,;]/.test(t);
}

/** First matching rule for a run, or null. */
export function ruleFor(text: string): { rule: DetectionRule; label: string } | null {
  const lower = text.toLowerCase();
  for (const rule of DETECTION_RULES) {
    for (const label of rule.labels) {
      const i = lower.indexOf(label);
      if (i === -1) continue;
      if (!isWholeStringMatch(lower, i, label.length)) continue;
      return { rule, label };
    }
  }
  return null;
}

/**
 * Propose placements across the document. Suggestions land just after the
 * label on the same line, matching where a human would drop the field.
 */
export function detectFields(runs: TextRun[]): Suggestion[] {
  const out: Suggestion[] = [];

  for (const run of runs) {
    if (!looksLikeLabel(run.text)) continue;
    const hit = ruleFor(run.text);
    if (!hit) continue;

    const size = ANCHOR_DEFAULT_SIZE[hit.rule.fieldType];
    const rawX = run.x + run.width + 0.012;
    out.push({
      fieldType: hit.rule.fieldType,
      matchedLabel: run.text.trim(),
      confidence: hit.rule.confidence,
      page: run.page,
      position_x: Number(Math.min(Math.max(rawX, 0), 1 - size.w).toFixed(4)),
      position_y: Number(Math.min(Math.max(run.y, 0), 1 - size.h).toFixed(4)),
      width: size.w,
      height: size.h,
    });
  }

  return out
    .sort((a, b) => (a.page - b.page) || (a.position_y - b.position_y)
      || (a.position_x - b.position_x));
}

/**
 * Drop suggestions that duplicate each other or an already-placed field.
 * Two labels on the same line ("Signature:" then "Date:") are distinct; the
 * same label detected twice by overlapping rules is not.
 */
export function dedupeSuggestions(
  suggestions: Suggestion[],
  existing: Array<{ page: number; position_x: number; position_y: number }> = [],
  tolerance = 0.02,
): Suggestion[] {
  const kept: Suggestion[] = [];
  for (const s of suggestions) {
    const clashesWithExisting = existing.some((e) =>
      e.page === s.page
      && Math.abs(e.position_x - s.position_x) < tolerance
      && Math.abs(e.position_y - s.position_y) < tolerance);
    if (clashesWithExisting) continue;

    const clashesWithKept = kept.some((k) =>
      k.page === s.page
      && Math.abs(k.position_x - s.position_x) < tolerance
      && Math.abs(k.position_y - s.position_y) < tolerance);
    if (clashesWithKept) continue;

    kept.push(s);
  }
  return kept;
}

/** One-line summary for the confirmation UI. */
export function describeSuggestions(suggestions: Suggestion[]): string {
  if (suggestions.length === 0) {
    return 'No signature blocks found — place fields by hand.';
  }
  const byType = new Map<FieldType, number>();
  for (const s of suggestions) {
    byType.set(s.fieldType, (byType.get(s.fieldType) ?? 0) + 1);
  }
  const parts = [...byType.entries()].map(([t, n]) => `${n} ${t}`);
  return `Found ${suggestions.length} likely field${suggestions.length === 1 ? '' : 's'}: ${parts.join(', ')}`;
}
